import React from "react";
import { useTranslation } from "react-i18next";
import { InfoCircle } from "@dreamhub/designsystem/icons";
import SingleSelect from "@/components/controls/SingleSelect";
import Checkbox from "@/components/ui/checkbox";
import IconContainer from "@/components/ui/icon-container";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/utils/utils";
import { bundleableTerms, parseBundleQuantity } from "../bundleUtils";
import { ServiceItem } from "../types";
import { formatCompletePricingText } from "../utils";

const TRANSLATION_KEY = "settings.product.bundleDrawer";

export interface BundleServiceSelection {
  termId: string;
  // Kept as typed, so the field can be empty mid-edit.
  quantity: string;
}

interface BundleServiceRowProps {
  service: ServiceItem;
  typeName: string;
  selection: BundleServiceSelection | undefined;
  onToggle: (checked: boolean) => void;
  onTermChange: (termId: string) => void;
  onQuantityChange: (quantity: string) => void;
}

export const BundleServiceRow: React.FC<BundleServiceRowProps> = ({
  service,
  typeName,
  selection,
  onToggle,
  onTermChange,
  onQuantityChange
}) => {
  const { t } = useTranslation();
  const terms = bundleableTerms(service);
  const isCustomized = terms.length === 0;
  const isChecked = Boolean(selection);

  const termText = (termIndex: number) =>
    formatCompletePricingText(
      terms[termIndex].price,
      service.pricingModel,
      terms[termIndex].billingFrequency,
      terms[termIndex].serviceItemPeriods,
      t,
      service.chargeType
    );

  let summary: string;
  if (isCustomized) {
    summary = t("settings.product.customPricingAndBilling");
  } else if (terms.length === 1) {
    summary = termText(0);
  } else {
    summary = t(`${TRANSLATION_KEY}.pricingTerms`, { count: terms.length });
  }

  const checkboxId = `bundle-service-${service.id}`;

  return (
    <div
      className="border-b-[0.5px] border-border-border1 last:border-b-0"
      data-test={`bundle-drawer-service-${service.id}`}
    >
      <div className="flex items-center">
        <div className="flex w-10 shrink-0 justify-center px-1.5">
          <Checkbox
            id={checkboxId}
            checked={isChecked}
            disabled={isCustomized}
            onCheckedChange={onToggle}
            data-test={`bundle-drawer-service-checkbox-${service.id}`}
          />
        </div>
        <label
          htmlFor={checkboxId}
          className={cn(
            "flex min-w-0 flex-1 flex-col gap-0.5 px-2.5 py-3",
            isCustomized ? "cursor-not-allowed" : "cursor-pointer"
          )}
        >
          <Text
            as="span"
            variant={isCustomized ? "bodyTertiary" : "body"}
            className="block truncate"
          >
            {service.name}
          </Text>
          <Text
            as="span"
            variant={isCustomized ? "smBodyTertiary" : "smBodySecondary"}
            className="block truncate"
          >
            {typeName ? `${typeName} • ${summary}` : summary}
          </Text>
        </label>
        <div className="flex w-[70px] shrink-0 justify-end px-1.5">
          {isCustomized && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  className="inline-flex"
                  data-test={`bundle-drawer-service-customized-${service.id}`}
                >
                  <IconContainer as="span" size="sm" color="tertiary">
                    <InfoCircle />
                  </IconContainer>
                  <span className="sr-only">
                    {t(`${TRANSLATION_KEY}.customizedTooltip`)}
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-56">
                {t(`${TRANSLATION_KEY}.customizedTooltip`)}
              </TooltipContent>
            </Tooltip>
          )}
          {selection && (
            <Input
              type="number"
              // Fractional quantities are allowed, as on a deal's services.
              step="any"
              inputMode="decimal"
              value={selection.quantity}
              onChange={(event) => onQuantityChange(event.target.value)}
              hasError={parseBundleQuantity(selection.quantity) === null}
              aria-label={t(`${TRANSLATION_KEY}.quantityLabel`, {
                name: service.name
              })}
              className="h-8 pl-2 pr-1"
              data-test={`bundle-drawer-service-quantity-${service.id}`}
            />
          )}
        </div>
      </div>
      {selection && terms.length > 1 && (
        <div
          className="flex min-w-0 flex-col gap-1 pb-4 pl-11 pr-1.5"
          data-test={`bundle-drawer-service-term-${service.id}`}
        >
          <Text variant="bodySecondary" className="pl-1">
            {t(`${TRANSLATION_KEY}.selectTerm`)}
          </Text>
          <SingleSelect
            fieldName={`bundle-term-${service.id}`}
            variant="form"
            options={terms.map((term, index) => ({
              id: term.id,
              displayName: termText(index)
            }))}
            selectedValue={selection.termId}
            onSelection={(termId) => onTermChange(String(termId))}
          />
        </div>
      )}
    </div>
  );
};
